import { Linkified } from "./Linkified";
import { motion } from "motion/react";
import { ArrowUp, MessageCircle } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { PEOPLE, type PersonId } from "../data/people";
import { spring } from "../lib/motion";
import { timeOf } from "../lib/time";
import { cn } from "../lib/utils";
import { useStore } from "../store";
import type { Task } from "../types";
import { Button } from "./ui/button";
import { Avatar } from "./ui/primitives";

/** Вопросы по задаче между постановщиком и монтажёром */
export function Chat({ task, me, title, bare, className }: { task: Task; me: PersonId; title?: string; bare?: boolean; className?: string }) {
  const send = useStore((s) => s.sendChat);
  const [text, setText] = useState("");
  const list = useRef<HTMLDivElement>(null);
  const other = me === task.creator ? task.editor : task.creator;

  useEffect(() => {
    list.current?.scrollTo({ top: list.current.scrollHeight, behavior: "smooth" });
  }, [task.chat.length]);

  const submit = () => {
    if (!text.trim()) return;
    send(task.id, me, text);
    setText("");
  };

  return (
    <div className={cn("flex flex-col gap-3", className)}>
      {!bare && (
        <div className="flex items-center gap-2">
          <MessageCircle className="size-4" />
          <span className="text-[15px] font-semibold">{title ?? `Вопросы ${me === task.creator ? "монтажёру" : "контент-мейкеру"}`}</span>
          {task.chat.length > 0 && <span className="text-xs text-muted-foreground">{task.chat.length}</span>}
        </div>
      )}
      {task.chat.length > 0 && (
        <div ref={list} className="scroll-thin flex max-h-48 flex-col gap-2 overflow-y-auto pr-1">
          {task.chat.map((m) => {
            const mine = m.from === me;
            return (
              <motion.div
                key={m.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={spring.smooth}
                className={cn("flex items-end gap-2", mine && "justify-end")}
              >
                {!mine && <Avatar id={m.from} size={24} />}
                <div
                  className={cn(
                    "max-w-[80%] whitespace-pre-wrap break-words px-3 py-2 text-[13px] leading-snug [overflow-wrap:anywhere]",
                    mine ? "rounded-[16px_6px_16px_16px] bg-primary text-primary-foreground" : "rounded-[6px_16px_16px_16px] bg-muted",
                  )}
                >
                  <Linkified text={m.text} />
                  <span className={cn("ml-2 text-[10px]", mine ? "text-ring" : "text-muted-foreground")}>{timeOf(m.at)}</span>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}
      <form
        className="flex h-11 items-center gap-2 rounded-full border border-input pl-4 pr-1.5 focus-within:ring-4 focus-within:ring-ring/20"
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Сообщение…"
          className="min-w-0 flex-1 bg-transparent text-[13px] outline-none placeholder:text-muted-foreground"
        />
        <Button type="submit" size="iconSm" disabled={!text.trim()} title="Отправить">
          <ArrowUp />
        </Button>
      </form>
    </div>
  );
}

